import { CSSProperties } from 'react';

import { appColors } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerQuestionFacts } from '@backrs_ui/grow-ui/src/models/models';

import {
  Tooltip,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  useMediaQuery,
  Theme,
  SxProps,
} from '@mui/material';

type FactsPopoverProps = Readonly<{
  isPopoverOpen: boolean;
  setIsPopoverOpen: (value: boolean) => void;
  facts: CareerQuestionFacts[];
  setHasSeenFirstQuestionFact?: (value: boolean) => void;
}>;

export const FactsPopover = ({
  isPopoverOpen,
  setIsPopoverOpen,
  facts,
  setHasSeenFirstQuestionFact,
}: FactsPopoverProps) => {
  const shouldPopoverMoveDown = useMediaQuery('(max-width:1300px)');

  const handleClose = () => {
    setIsPopoverOpen(false);

    if (setHasSeenFirstQuestionFact) {
      setHasSeenFirstQuestionFact(true);
    }
  };

  const handleOpen = () => {
    setIsPopoverOpen(true);
  };

  const popoverContent = (
    <List sx={listContainerStyle}>
      {facts.map((info, index) => (
        <ListItem key={index} sx={listItemsStyle}>
          <ListItemText
            primary={
              <Typography
                sx={info.type === 'LINK' ? urlTextStyle : regularTextStyle}
                onClick={info.type === 'LINK' ? () => window.open(info.url, '_blank') : undefined}
              >
                {info.value}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <Tooltip
            onClose={handleClose}
            open={isPopoverOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            arrow
            placement={shouldPopoverMoveDown ? 'bottom' : 'right-start'}
            title={popoverContent}
            PopperProps={
              shouldPopoverMoveDown
                ? undefined
                : {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [-20, 0],
                        },
                      },
                    ],
                  }
            }
            componentsProps={{
              popper: {
                sx: popperSx,
              },
              tooltip: {
                sx: tooltipSx,
              },
              arrow: {
                // we target the before pseudo element of the arrow to style it correctly
                sx: {
                  ...arrowContainerSx,
                  '&:before': arrowSx,
                },
              },
            }}
          >
            <Button
              variant="contained"
              onClick={() => (isPopoverOpen ? handleClose() : handleOpen())}
              sx={buttonContainerStyle}
              id="facts-button"
            >
              <Typography sx={factsTextStyle}>{appStrings.modules.survey.facts}</Typography>
            </Button>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};

const listContainerStyle: SxProps<Theme> = {
  pl: 3,
} as const;

const listItemsStyle: SxProps<Theme> = {
  display: 'list-item',
  listStyleType: 'disc',
  pr: 1,
  pl: 0.5,
  py: 0,
} as const;

const urlTextStyle: SxProps<Theme> = {
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: '11pt',
  lineHeight: '1.3',
} as const;

const regularTextStyle: SxProps<Theme> = {
  fontSize: '11pt',
  lineHeight: '1.3',
} as const;

const popperSx: SxProps<Theme> = {
  // shift the arrow up to align with the FACT
  '&[data-popper-placement^="right"] .MuiTooltip-arrow': {
    transform: 'translateY(16px) !important',
  },
} as const;

const tooltipSx: CSSProperties = {
  backgroundColor: appColors.color.orange1,
  color: appColors.neutral.white,
  border: `3px solid ${appColors.neutral.black}`,
  fontSize: '14px',
  borderRadius: 4,
  padding: 1,
  whiteSpace: 'normal',
} as const;

const arrowContainerSx: CSSProperties = {
  color: appColors.color.orange1,
  fontSize: 30,
} as const;

const arrowSx: CSSProperties = {
  border: '3px solid #000',
  borderRadius: 1,
} as const;

const buttonContainerStyle: SxProps<Theme> = {
  backgroundColor: appColors.color.orange1,
  borderRadius: 8,
  paddingX: 3,
  paddingY: 0.5,
  '&:hover': {
    backgroundColor: appColors.color.orange1,
  },
} as const;

const factsTextStyle: SxProps<Theme> = {
  color: appColors.neutral.white,
  fontWeight: 700,
  fontSize: '14px',
} as const;
